/**
 * Canvas-block schema (UI-1) — typed structured deliverable components.
 *
 * Agents emit blocks inline in markdown using fenced code blocks tagged
 * with canvas:<type>. The parser splits the markdown content into an
 * interleaved list of markdown chunks and typed blocks. Unknown block
 * types degrade gracefully to the raw fence so the user still sees
 * something useful.
 */

export interface ExecutiveSummaryBlock {
  type: "executive_summary"
  title?: string
  body: string
  highlights?: string[]
}

export interface ClaimWithCitationBlock {
  type: "claim_with_citation"
  claim: string
  confidence?: string
  citations: string[]
}

export interface ComparisonTableBlock {
  type: "comparison_table"
  title?: string
  columns: string[]
  rows: (string | number | null)[][]
  citations?: (string[] | null)[][]
}

export interface DecisionMatrixBlock {
  type: "decision_matrix"
  title?: string
  options: string[]
  criteria: { name: string; weight?: number }[]
  scores: number[][]
}

export interface QuoteCardBlock {
  type: "quote_card"
  quote: string
  attribution?: string
  source?: string
}

export interface TimelineBlock {
  type: "timeline"
  title?: string
  entries: { date: string; text: string; source?: string }[]
}

export interface ChartBlock {
  type: "chart"
  title?: string
  chartType: "bar"
  series: { label: string; value: number }[]
  unit?: string
}

export interface SourcePanelBlock {
  type: "source_panel"
  sources: { title: string; url?: string; usageCount?: number; confidence?: string }[]
}

export interface MarkdownBlock {
  type: "markdown"
  content: string
}

export type CanvasBlock =
  | ExecutiveSummaryBlock
  | ClaimWithCitationBlock
  | ComparisonTableBlock
  | DecisionMatrixBlock
  | QuoteCardBlock
  | TimelineBlock
  | ChartBlock
  | SourcePanelBlock
  | MarkdownBlock

const KNOWN_TYPES: ReadonlySet<CanvasBlock["type"]> = new Set([
  "executive_summary",
  "claim_with_citation",
  "comparison_table",
  "decision_matrix",
  "quote_card",
  "timeline",
  "chart",
  "source_panel",
  "markdown",
])

const FENCE_RE = /^```canvas:([a-z_]+)\s*\r?\n([\s\S]*?)\r?\n```/gm

export function parseCanvasBlocks(content: string): CanvasBlock[] {
  if (!content) return []
  const out: CanvasBlock[] = []
  let lastIndex = 0
  FENCE_RE.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = FENCE_RE.exec(content)) !== null) {
    const [full, rawType, rawJson] = match
    const start = match.index
    if (start > lastIndex) {
      const md = content.slice(lastIndex, start)
      if (md.trim().length > 0) out.push({ type: "markdown", content: md })
    }
    const type = rawType.toLowerCase() as CanvasBlock["type"]
    if (!KNOWN_TYPES.has(type) || type === "markdown") {
      out.push({ type: "markdown", content: full })
    } else {
      try {
        const parsed = JSON.parse(rawJson) as Record<string, unknown>
        out.push({ ...parsed, type } as CanvasBlock)
      } catch {
        out.push({ type: "markdown", content: full })
      }
    }
    lastIndex = start + full.length
  }
  if (lastIndex < content.length) {
    const tail = content.slice(lastIndex)
    if (tail.trim().length > 0) out.push({ type: "markdown", content: tail })
  }
  return out
}

export function hasCanvasBlocks(content: string): boolean {
  if (!content) return false
  return /```canvas:[a-z_]+\s*\r?\n[\s\S]*?\r?\n```/m.test(content)
}
